import { AppstoreOutlined, SaveOutlined } from '@ant-design/icons'
import { Button, Form } from 'antd'
import { t } from 'i18next'
import { useCallback, useState } from 'react'
import {
  DetailsPageLayout,
  MultiColumnFormContentContainer,
  SelectLanguage,
  SimpleSwitch,
  Spacer,
  SubHeader,
} from '../../components'
import { NO_DATA_PLACEHOLDER, urlList } from '../../constants/constants'
import {
  useCurrentUser,
  useLanguage,
  useTheme,
  useUserDetails,
} from '../../hooks'
import { themeColorKeys } from '../../theme'
import { Language } from '../../types/generalTypes'

const SettingsPage = () => {
  const [saveLoading, setSaveLoading] = useState(false)

  const { currentUser } = useCurrentUser({ preventFetch: true })
  const { patchUser } = useUserDetails({ userId: currentUser?.id })
  const { selectedTheme, toggleTheme } = useTheme()
  const { selectedLanguage, toggleLanguage } = useLanguage()

  const appVersion = process.env.REACT_APP_VERSION || NO_DATA_PLACEHOLDER

  const handleSave = useCallback(async () => {
    if (!currentUser?.id || !selectedLanguage) return

    try {
      if (selectedLanguage !== currentUser?.language) {
        patchUser(
          `${urlList.USERS}${currentUser.id}`,
          {
            language: selectedLanguage,
          },
          setSaveLoading,
          t('userDetailsPage.userSavedSuccess') || ''
        )
      }
    } catch (error) {
      console.error('Error updating user', error)
    }
  }, [currentUser, patchUser, selectedLanguage])

  const handleLanguageChange = useCallback(
    (value: Language | undefined) => {
      if (value) {
        toggleLanguage(value)
      }
    },
    [toggleLanguage]
  )

  return (
    <DetailsPageLayout>
      <SubHeader>
        <AppstoreOutlined />
        {t('settingsPage.general')}
        <Spacer />
        <Button
          type="primary"
          icon={<SaveOutlined />}
          htmlType="submit"
          loading={saveLoading}
          onClick={handleSave}
        >
          {t('saveButton')}
        </Button>
      </SubHeader>
      <MultiColumnFormContentContainer>
        <Form layout="vertical" onFinish={handleSave} requiredMark={false}>
          <SelectLanguage
            value={selectedLanguage || 'en'}
            onChange={handleLanguageChange}
          />
          <Form.Item
            label={t('settingsPage.currentProductVersion')}
            name="version"
          >
            {appVersion}
          </Form.Item>
          <Form.Item label={t('settingsPage.nightMode')} name="nightMode">
            <SimpleSwitch
              checked={selectedTheme === themeColorKeys.dark}
              onClick={toggleTheme}
            />
          </Form.Item>
        </Form>
      </MultiColumnFormContentContainer>
    </DetailsPageLayout>
  )
}

export default SettingsPage
